"use client";
/**
 * SANATAN DECODE — style proof v2. 20s @ 30fps, 1280x720.
 * v1 notes from Yash: ground was too dark, and pure vector reads boring.
 * v2 = light paper ground + gpt-image-2 flat-illustration cutouts (white keyed out
 * locally; gpt-image-2 refuses background:"transparent") carrying the frame, with the
 * graphics layer on top. Text density deliberately cut — one idea per beat, no labels
 * on things the picture already says.
 * Silent by design.
 */
import React from "react";
import {
  AbsoluteFill,
  Img,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
} from "remotion";
import { loadFont as loadDeva } from "@remotion/google-fonts/NotoSansDevanagari";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: deva } = loadDeva("normal", {
  weights: ["400", "600", "700", "800", "900"],
  subsets: ["devanagari", "latin"],
});
const { fontFamily: inter } = loadInter("normal", {
  weights: ["500", "700", "800"],
  subsets: ["latin"],
});

/* ── locked palette (light) ───────────────────────────────────── */
const INK = "#1B2A4A";
const SAFFRON = "#D98324";
const TERRA = "#C4553D";
const MUTED = "#8A8578";
const RULE = "rgba(27,42,74,0.16)";

const SHADOW = "drop-shadow(0 16px 22px rgba(27,42,74,0.20))";

/* ── ground: the generated paper, drifting ────────────────────── */
const Ground: React.FC = () => {
  const frame = useCurrentFrame();
  const s = interpolate(frame, [0, 600], [1.06, 1.14]);
  const x = interpolate(frame, [0, 600], [-14, 14]);
  return (
    <AbsoluteFill style={{ backgroundColor: "#F4EDDF", overflow: "hidden" }}>
      <Img
        src={staticFile("sanatan/bg_paper.png")}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: `scale(${s}) translateX(${x}px)`,
        }}
      />
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(ellipse 78% 70% at 50% 45%, rgba(255,252,244,0.55) 0%, rgba(212,196,166,0.34) 100%)",
        }}
      />
    </AbsoluteFill>
  );
};

const Grain: React.FC = () => (
  <AbsoluteFill style={{ pointerEvents: "none" }}>
    <svg width="100%" height="100%" style={{ opacity: 0.11, mixBlendMode: "multiply" }}>
      <filter id="g2">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" />
      </filter>
      <rect width="100%" height="100%" filter="url(#g2)" />
    </svg>
  </AbsoluteFill>
);

const Furniture: React.FC = () => {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();
  const p = interpolate(frame, [0, durationInFrames], [0, 1]);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <div style={{ position: "absolute", top: 32, left: 46, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 5, height: 20, background: SAFFRON, borderRadius: 3 }} />
        <span style={{ fontFamily: deva, fontWeight: 700, fontSize: 18, letterSpacing: 3, color: INK, opacity: 0.75 }}>
          सनातन डिकोड
        </span>
      </div>
      <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: "100%", background: RULE }} />
      <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: `${p * 100}%`, background: SAFFRON }} />
    </AbsoluteFill>
  );
};

/* ── a cutout that lands, then drifts (parallax depth) ────────── */
const Cutout: React.FC<{
  src: string;
  h?: number;
  w?: number;
  left?: number;
  top?: number;
  at?: number;
  from?: number;
  drift?: number;
  rotate?: number;
}> = ({ src, h, w, left = 0, top = 0, at = 0, from = 44, drift = 10, rotate = 0 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const s = spring({ frame: frame - at, fps, config: { damping: 20, stiffness: 90, mass: 1.1 } });
  const d = interpolate(frame - at, [0, 200], [0, drift], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <Img
      src={staticFile(src)}
      style={{
        position: "absolute",
        left,
        top,
        ...(h ? { height: h } : {}),
        ...(w ? { width: w } : {}),
        opacity: s,
        filter: SHADOW,
        transform: `translateY(${(1 - s) * from - d}px) scale(${0.96 + s * 0.04}) rotate(${rotate}deg)`,
      }}
    />
  );
};

/* ── kinetic Devanagari ───────────────────────────────────────── */
const Line: React.FC<{
  words: string[];
  at?: number;
  stagger?: number;
  size?: number;
  color?: string;
  weight?: number;
  redIdx?: number[];
  strikeIdx?: number[];
  strikeAt?: number;
  justify?: "center" | "flex-start";
}> = ({
  words,
  at = 0,
  stagger = 5,
  size = 62,
  color = INK,
  weight = 800,
  redIdx = [],
  strikeIdx = [],
  strikeAt = 0,
  justify = "center",
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const strike = interpolate(frame, [strikeAt, strikeAt + 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  return (
    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: justify, gap: "0 16px" }}>
      {words.map((w, i) => {
        const sp = spring({ frame: frame - (at + i * stagger), fps, config: { damping: 18, stiffness: 150, mass: 0.6 } });
        return (
          <span
            key={i}
            style={{
              position: "relative",
              display: "inline-block",
              fontFamily: deva,
              fontWeight: weight,
              fontSize: size,
              lineHeight: 1.4,
              color: redIdx.includes(i) ? TERRA : color,
              opacity: sp,
              transform: `translateY(${(1 - sp) * 22}px)`,
            }}
          >
            {w}
            {strikeIdx.includes(i) && (
              <span
                style={{
                  position: "absolute",
                  left: -4,
                  top: "53%",
                  height: Math.max(4, size * 0.07),
                  width: `calc((100% + 8px) * ${strike})`,
                  background: TERRA,
                  borderRadius: 4,
                }}
              />
            )}
          </span>
        );
      })}
    </div>
  );
};

/* ══ BEAT 1 — the hook. Manuscript carries the frame. ═══════════ */
const Beat1: React.FC = () => (
  <AbsoluteFill>
    <Cutout src="sanatan/manuscript.png" w={720} left={300} top={352} at={26} from={60} drift={14} rotate={-2} />
    <div style={{ position: "absolute", left: 0, right: 0, top: 118 }}>
      <Line words={["आप", "जिस", "गोत्र", "का", "नाम", "लेते", "हैं"]} at={4} stagger={5} size={40} color={MUTED} weight={600} />
      <div style={{ height: 14 }} />
      <Line
        words={["वो", "आपका", "सरनेम", "नहीं", "है।"]}
        at={50}
        stagger={7}
        size={76}
        weight={900}
        redIdx={[2, 3, 4]}
        strikeIdx={[2]}
        strikeAt={96}
      />
    </div>
  </AbsoluteFill>
);

/* ══ BEAT 2 — one blood-line, ऋषि → आप. Almost no text. ════════ */
const Beat2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const Y = 402;
  const X0 = 372;
  const X1 = 930;

  const draw = interpolate(frame, [40, 118], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.inOut(Easing.cubic),
  });
  const label = spring({ frame: frame - 120, fps, config: { damping: 19, stiffness: 110 } });

  return (
    <AbsoluteFill>
      <Cutout src="sanatan/rishi.png" h={340} left={150} top={214} at={6} drift={8} />
      <Cutout src="sanatan/modern_man.png" h={360} left={952} top={196} at={104} drift={8} />

      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <line x1={X0} y1={Y} x2={X0 + (X1 - X0) * draw} y2={Y} stroke={SAFFRON} strokeWidth={5} strokeLinecap="round" />
        {[0.22, 0.45, 0.68].map((t, i) => {
          const x = X0 + (X1 - X0) * t;
          const on = draw > t ? 1 : 0;
          const sp = spring({ frame: frame - (40 + 78 * t), fps, config: { damping: 15, stiffness: 180 } });
          return <circle key={i} cx={x} cy={Y} r={10 * sp * on} fill="#F6F0E4" stroke={SAFFRON} strokeWidth={4} />;
        })}
      </svg>

      <div
        style={{
          position: "absolute",
          left: X0,
          width: X1 - X0,
          top: Y - 152,
          textAlign: "center",
          opacity: label,
          transform: `translateY(${(1 - label) * 18}px)`,
        }}
      >
        <div style={{ fontFamily: deva, fontSize: 58, fontWeight: 900, color: INK }}>एक ही रक्त-रेखा</div>
      </div>

      <div style={{ position: "absolute", left: 150, top: 566, width: 250, textAlign: "center", fontFamily: deva, fontSize: 26, fontWeight: 700, color: MUTED }}>
        ऋषि
      </div>
      <div style={{ position: "absolute", left: 930, top: 566, width: 250, textAlign: "center", fontFamily: deva, fontSize: 26, fontWeight: 700, color: MUTED }}>
        आप
      </div>
    </AbsoluteFill>
  );
};

/* ══ BEAT 3 — the rule, tagged शास्त्र. ════════════════════════ */
const Beat3: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const slab = spring({ frame: frame - 30, fps, config: { damping: 21, stiffness: 100, mass: 1 } });
  const tag = spring({ frame: frame - 40, fps, config: { damping: 16, stiffness: 150 } });
  const wipe = interpolate(frame, [52, 78], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });

  return (
    <AbsoluteFill>
      <Cutout src="sanatan/couple.png" w={560} left={360} top={64} at={4} from={54} drift={9} />
      <div
        style={{
          position: "absolute",
          left: 168,
          top: 486,
          width: 944,
          padding: "26px 40px",
          background: "rgba(255,253,247,0.97)",
          borderLeft: `9px solid ${SAFFRON}`,
          borderRadius: 5,
          boxShadow: "0 14px 30px rgba(27,42,74,0.14)",
          opacity: slab,
          transform: `translateY(${(1 - slab) * 26}px)`,
        }}
      >
        <div
          style={{
            display: "inline-block",
            fontFamily: deva,
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: 4,
            color: "#FFF8EC",
            background: INK,
            padding: "5px 16px",
            borderRadius: 4,
            opacity: tag,
          }}
        >
          शास्त्र
        </div>
        <div style={{ height: 12 }} />
        <div
          style={{
            fontFamily: deva,
            fontSize: 72,
            fontWeight: 900,
            color: INK,
            clipPath: `inset(0 ${(1 - wipe) * 100}% 0 0)`,
          }}
        >
          सगोत्र विवाह नहीं।
        </div>
      </div>
    </AbsoluteFill>
  );
};

/* ══ BEAT 4 — the mechanism, then the word. ════════════════════ */
const Beat4: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const CX = 452;
  const PY = 216;
  const CY = 372;

  const pA = spring({ frame: frame - 6, fps, config: { damping: 16, stiffness: 150 } });
  const pB = spring({ frame: frame - 14, fps, config: { damping: 16, stiffness: 150 } });
  const link = interpolate(frame, [28, 46], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  const kid = spring({ frame: frame - 46, fps, config: { damping: 14, stiffness: 160 } });
  const term = spring({ frame: frame - 64, fps, config: { damping: 19, stiffness: 105 } });

  // alleles are BARS, not dots — two dots inside a ring reads as a face.
  const Allele: React.FC<{ x: number; fill: string; opacity?: number }> = ({ x, fill, opacity = 1 }) => (
    <rect x={x - 7} y={-21} width={14} height={42} rx={7} fill={fill} opacity={opacity} />
  );

  const Carrier: React.FC<{ x: number; s: number }> = ({ x, s }) => (
    <g opacity={s} transform={`translate(${x},${PY}) scale(${0.86 + s * 0.14})`}>
      <circle r={46} fill="#FBF6EA" stroke={INK} strokeWidth={4} opacity={0.9} />
      <Allele x={-15} fill={INK} opacity={0.26} />
      <Allele x={15} fill={TERRA} />
    </g>
  );

  return (
    <AbsoluteFill>
      <Cutout src="sanatan/dna.png" h={556} left={928} top={88} at={2} drift={12} />
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Carrier x={CX - 132} s={pA} />
        <Carrier x={CX + 132} s={pB} />
        <line x1={CX - 112} y1={PY + 40} x2={CX - 112 + 112 * link} y2={PY + 40 + (CY - PY - 40) * link} stroke={SAFFRON} strokeWidth={4} strokeLinecap="round" />
        <line x1={CX + 112} y1={PY + 40} x2={CX + 112 - 112 * link} y2={PY + 40 + (CY - PY - 40) * link} stroke={SAFFRON} strokeWidth={4} strokeLinecap="round" />
        <g opacity={kid} transform={`translate(${CX},${CY}) scale(${0.84 + kid * 0.16})`}>
          <circle r={48} fill={TERRA} opacity={0.13} />
          <circle r={48} fill="none" stroke={TERRA} strokeWidth={5} />
          <Allele x={-15} fill={TERRA} />
          <Allele x={15} fill={TERRA} />
        </g>
      </svg>

      <div style={{ position: "absolute", left: 92, top: 476, width: 720, textAlign: "center", opacity: term, transform: `translateY(${(1 - term) * 18}px)` }}>
        <div style={{ fontFamily: inter, fontSize: 44, fontWeight: 800, letterSpacing: 6, color: TERRA }}>CONSANGUINITY</div>
        <div style={{ height: 12 }} />
        <div style={{ fontFamily: deva, fontSize: 27, fontWeight: 700, color: INK, opacity: 0.82 }}>
          दो हज़ार साल बाद, विज्ञान ने इसे एक नाम दिया।
        </div>
      </div>
    </AbsoluteFill>
  );
};

export const SanatanDecodeGotraV2: React.FC = () => (
  <AbsoluteFill style={{ backgroundColor: "#F4EDDF" }}>
    <Ground />
    <Sequence from={0} durationInFrames={150}>
      <Beat1 />
    </Sequence>
    <Sequence from={150} durationInFrames={180}>
      <Beat2 />
    </Sequence>
    <Sequence from={330} durationInFrames={150}>
      <Beat3 />
    </Sequence>
    <Sequence from={480} durationInFrames={120}>
      <Beat4 />
    </Sequence>
    <Grain />
    <Furniture />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
